<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useForm } from 'vee-validate';
import { employmentSchema } from '@/validation';
import type { ApplicantIncome, EmploymentEntry, EmploymentTypeEnum, PeriodicValue } from '@/types';
import { EmploymentType, brokerEmploymentTypeOptions } from '@/helpers/const';
import { defaultPeriodicValue } from '@/helpers/builder';
import { TrashIcon } from '@heroicons/vue/24/outline';
import SalaryForm from './SalaryForm.vue';
import SelfEmployedForm from './SelfEmployedForm.vue';

const props = withDefaults(
  defineProps<{
    applicantId: string;
    modelValue: EmploymentEntry;
    isMultiple?: boolean;
    cardIndex?: number;
    retiredIncomeData?: Partial<ApplicantIncome>;
  }>(),
  {
    isMultiple: false,
    cardIndex: 0,
    retiredIncomeData: undefined,
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', v: EmploymentEntry): void;
  (e: 'save'): void;
  (e: 'remove', id: string): void;
  (e: 'employmentType', type: EmploymentTypeEnum): void;
  (
    e: 'retiredIncome',
    v: {
      centrelinkPayments?: PeriodicValue;
      childSupportMaintenance?: PeriodicValue;
      familyAllowance?: PeriodicValue;
      familyTaxBenefit?: PeriodicValue;
      governmentPension?: PeriodicValue;
      dividendsInvestments?: PeriodicValue;
      superannuation?: PeriodicValue;
      other?: PeriodicValue;
    },
  ): void;
}>();

const actionSection = ref<HTMLElement>();

/** Build a Record<string, string> for BxSelect from employment options */
const selectOptions = computed(() => {
  const source = brokerEmploymentTypeOptions;
  const entries = Object.entries(source) as [EmploymentTypeEnum, { label: string }][];
  const filtered = props.isMultiple
    ? entries.filter(([key]) => key !== EmploymentType.RETIRED && key !== EmploymentType.UNEMPLOYED)
    : entries;

  return Object.fromEntries(filtered.map(([key, val]) => [key, val.label])) as Record<string, string>;
});

const { values, handleSubmit, resetForm } = useForm<EmploymentEntry>({
  validationSchema: employmentSchema,
  validateOnMount: false,
});

const employmentInput = ref<EmploymentEntry>(props.modelValue);
const salaryRef = ref<InstanceType<typeof SalaryForm>>();
const selfEmployedRef = ref<InstanceType<typeof SelfEmployedForm>>();

const newItem = ref(!props.modelValue.type);
const showEntry = ref(!props.modelValue.type);

const cardTitle = computed(() => {
  const source = brokerEmploymentTypeOptions;
  const desc = values.type ? source[values.type]?.label ?? '' : '';
  const name =
    values.type === EmploymentType.SELF_EMPLOYED
      ? values.tradingName ?? ''
      : values.type === EmploymentType.FULL_TIME ||
        values.type === EmploymentType.PART_TIME ||
        values.type === EmploymentType.CASUAL
      ? values.employerName ?? ''
      : '';
  return newItem.value ? 'Source of income' : name ? `${desc} - ${name}` : desc;
});

const editItem = () => {
  showEntry.value = true;
};

/** Opens the card and scrolls to the action section */
const expandAndFocus = () => {
  showEntry.value = true;
  setTimeout(() => {
    actionSection.value?.scrollIntoView({
      behavior: 'smooth',
      block: 'end',
    });
  }, 300);
};
const checkEmployeeTypeIsValid = handleSubmit(
  () => true,
  () => false,
);

/** Validates the employment card and emits the updated value */
const update = async () => {
  if (!(await checkEmployeeTypeIsValid())) {
    return false;
  }

  let saved = false;

  switch (values.type) {
    case EmploymentType.RETIRED: {
      // vee-validate registers BxField values at runtime regardless of EmploymentEntry typing
      const retiredValues = values as typeof values & {
        centrelinkPayments?: PeriodicValue;
        childSupportMaintenance?: PeriodicValue;
        familyAllowance?: PeriodicValue;
        familyTaxBenefit?: PeriodicValue;
        governmentPension?: PeriodicValue;
        dividendsInvestments?: PeriodicValue;
        superannuation?: PeriodicValue;
        other?: PeriodicValue;
      };
      // untouched fields are undefined in the form; default them so they never overwrite stored values
      emit('retiredIncome', {
        centrelinkPayments: retiredValues.centrelinkPayments ?? defaultPeriodicValue(),
        childSupportMaintenance: retiredValues.childSupportMaintenance ?? defaultPeriodicValue(),
        familyAllowance: retiredValues.familyAllowance ?? defaultPeriodicValue(),
        familyTaxBenefit: retiredValues.familyTaxBenefit ?? defaultPeriodicValue(),
        governmentPension: retiredValues.governmentPension ?? defaultPeriodicValue(),
        dividendsInvestments: retiredValues.dividendsInvestments ?? defaultPeriodicValue(),
        superannuation: retiredValues.superannuation ?? defaultPeriodicValue(),
        other: retiredValues.other ?? defaultPeriodicValue(),
      });
      emit('update:modelValue', { id: props.modelValue.id, type: values.type });
      saved = true;
      break;
    }
    case EmploymentType.UNEMPLOYED:
      emit('update:modelValue', { id: props.modelValue.id, type: values.type });
      saved = true;
      break;
    case EmploymentType.SELF_EMPLOYED:
      saved = (await selfEmployedRef.value?.update()) ?? false;
      if (saved) {
        emit('update:modelValue', employmentInput.value);
      }
      break;
    default:
      saved = (await salaryRef.value?.update()) ?? false;
      if (saved) {
        emit('update:modelValue', employmentInput.value);
      }
      break;
  }

  if (saved) {
    showEntry.value = false;
    newItem.value = false;
  }

  return saved;
};

const cancelChanges = () => {
  salaryRef.value?.cancel();
  selfEmployedRef.value?.cancel();
  if (newItem.value) {
    if (props.cardIndex !== 0) {
      removeItem();
    } else {
      resetForm({ values: { id: props.modelValue.id, elementId: props.modelValue.elementId } as EmploymentEntry });
    }
  } else {
    resetForm();
    showEntry.value = false;
  }
};

const showModal = ref(false);
const openModal = () => {
  showModal.value = true;
};
const removeItem = () => {
  showModal.value = false;
  emit('remove', props.modelValue.elementId || props.modelValue.id || '');
};

defineExpose({ update, cancel: cancelChanges, expandAndFocus });

watch(
  () => props.modelValue,
  (val) => {
    const formValues =
      val.type === EmploymentType.RETIRED && props.retiredIncomeData ? { ...val, ...props.retiredIncomeData } : val;
    resetForm({ values: formValues as EmploymentEntry });
    employmentInput.value = props.modelValue;
  },
  { immediate: true, deep: true },
);

watch(
  () => props.retiredIncomeData,
  (incomeData) => {
    if (values.type === EmploymentType.RETIRED && incomeData) {
      resetForm({ values: { ...values, ...incomeData } as EmploymentEntry });
    }
  },
);

watch(
  () => values.type,
  (val) => {
    if (val) {
      emit('employmentType', val);
    }
  },
);
</script>

<template>
  <div>
    <BxExpander v-model="showEntry" separator>
      <template #header>
        <div class="px-4 py-5 sm:px-6">
          <div class="flex flex-wrap items-center justify-between sm:flex-nowrap">
            <h3 class="text-base font-semibold text-gray-900">{{ cardTitle }}</h3>
            <div v-if="!showEntry" class="flex-shrink-0">
              <BxButton variant="secondary" @click="editItem()">Edit</BxButton>
              <BxButton v-if="props.isMultiple" variant="link" class="ml-4" @click="openModal()"> Remove </BxButton>
            </div>
          </div>
        </div>
      </template>
      <template #body>
        <div class="mt-3 p-6">
          <BxField v-slot="{ field }" name="type">
            <BxRadio
              v-bind="field"
              :id="props.applicantId + '-job-type'"
              :options="selectOptions"
              :block="true"
              label="Please select the applicant's employment status"
              label-type="default"
              class="max-w-md"
            />
          </BxField>

          <div v-if="values.type" class="mt-6">
            <Transition name="fade">
              <SalaryForm
                v-if="
                  values.type === EmploymentType.FULL_TIME ||
                  values.type === EmploymentType.PART_TIME ||
                  values.type === EmploymentType.CASUAL
                "
                ref="salaryRef"
                v-model="employmentInput"
                :applicant-id="props.applicantId"
                :type="values.type"
              />
              <SelfEmployedForm
                v-else-if="values.type === EmploymentType.SELF_EMPLOYED"
                ref="selfEmployedRef"
                v-model="employmentInput"
                :applicant-id="props.applicantId"
              />

              <div v-else-if="values.type === EmploymentType.RETIRED">
                <div class="mt-8">
                  <BxField v-slot="{ field }" name="centrelinkPayments">
                    <BxPeriodicAmount
                      v-bind="field"
                      :id="applicantId + '-centerlink'"
                      label="Centrelink payments"
                      label-type="floating"
                      leading-symbol="$"
                      class="form-control mt-4"
                    />
                  </BxField>
                  <BxField v-slot="{ field }" name="childSupportMaintenance">
                    <BxPeriodicAmount
                      v-bind="field"
                      :id="applicantId + '-child-support'"
                      label="Child support maintenance"
                      label-type="floating"
                      leading-symbol="$"
                      class="form-control"
                    />
                  </BxField>
                  <BxField v-slot="{ field }" name="familyAllowance">
                    <BxPeriodicAmount
                      v-bind="field"
                      :id="applicantId + '-family-allowance'"
                      label="Family allowance"
                      label-type="floating"
                      leading-symbol="$"
                      class="form-control"
                    />
                  </BxField>
                  <BxField v-slot="{ field }" name="familyTaxBenefit">
                    <BxPeriodicAmount
                      v-bind="field"
                      :id="applicantId + '-family-tax-benefits'"
                      label="Family tax benefits"
                      label-type="floating"
                      leading-symbol="$"
                      class="form-control"
                    />
                  </BxField>
                  <BxField v-slot="{ field }" name="governmentPension">
                    <BxPeriodicAmount
                      v-bind="field"
                      :id="applicantId + '-government-pension'"
                      label="Government pension"
                      label-type="floating"
                      leading-symbol="$"
                      class="form-control"
                    />
                  </BxField>
                  <BxField v-slot="{ field }" name="dividendsInvestments">
                    <BxPeriodicAmount
                      v-bind="field"
                      :id="applicantId + '-dividends'"
                      label="Dividends & investments"
                      label-type="floating"
                      leading-symbol="$"
                      class="form-control"
                    />
                  </BxField>
                  <BxField v-slot="{ field }" name="superannuation">
                    <BxPeriodicAmount
                      v-bind="field"
                      :id="applicantId + '-superannuation'"
                      label="Superannuation"
                      label-type="floating"
                      leading-symbol="$"
                      class="form-control"
                    />
                  </BxField>
                  <BxField v-slot="{ field }" name="other">
                    <BxPeriodicAmount
                      v-bind="field"
                      :id="applicantId + '-other'"
                      label="Other"
                      label-type="floating"
                      leading-symbol="$"
                      class="form-control"
                    />
                  </BxField>
                </div>
              </div>
              <div v-else></div>
            </Transition>
          </div>

          <div ref="actionSection" class="mt-6 flex-shrink-0">
            <BxButton :id="props.applicantId + '-income-save'" variant="secondary" @click="update()"> Save </BxButton>
            <BxButton variant="link" class="ml-4" @click="cancelChanges()">Cancel</BxButton>
          </div>
        </div>
      </template>
    </BxExpander>

    <BxModal
      :open="showModal"
      :icon="TrashIcon"
      title="Remove employment"
      confirm-label="Remove"
      cancel-label="Cancel"
      @close="showModal = false"
      @confirm="removeItem()"
    >
      Do you want to remove
      <span class="text-bridgit-green">{{ cardTitle }}</span
      >?
    </BxModal>
  </div>
</template>
